<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">Companies</h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
      </div>
    </base-header>
    <div class="container-fluid mt--6">
      <div>
        <card
          class="no-border-card"
          body-classes="px-0 pb-1"
          footer-classes="pb-2"
        >
          <template slot="header">
            <div class="row align-items-center">
              <div class="col-lg-4 col-md-6 col-8">
                <h3 class="mb-0" id="companies_heading">Companies</h3>
              </div>
              <div class="col-lg-6 col-md-4 col-12 mb-lg-0 mb-2">
                <div class="form-group mb-0">
                  <div class="input-group input-group-merge">
                    <div class="input-group-prepend">
                      <span class="input-group-text">
                        <i class="fa fa-search"></i>
                      </span>
                    </div>
                    <input
                      type="text"
                      class="form-control"
                      placeholder="Search companies..."
                      v-model="searchQuery"
                      @input="onSearchInput"
                    >
                  </div>
                </div>
              </div>
              <div class="col-lg-2 col-md-2 col-4 text-right">
                <base-button
                  @click="modalNewCompany = true"
                  type="primary"
                  size="sm"
                >
                  <i class="fas fa-plus mr-1"></i>
                  Add Company
                </base-button>
              </div>
            </div>
          </template>

          <el-table
            class="table-responsive align-items-center table-flush table-striped"
            header-row-class-name="thead-light"
            :data="companiesData"
            @row-click="selectCompany"
            v-loading="loading"
          >
            <el-table-column label="Company Name" min-width="200" prop="company_name">
              <template v-slot="{ row }">
                <router-link
                  class="font-weight-600 name-col"
                  :to="{ name: 'CompanyDetails', params: { id: row.id } }"
                >
                  {{ row.company_name }}
                </router-link>
              </template>
            </el-table-column>
            <el-table-column label="Industry" min-width="150" prop="industry">
              <template v-slot="{ row }">
                {{ row.industry || 'Not specified' }}
              </template>
            </el-table-column>
            <el-table-column label="Primary Market" min-width="120" prop="primary_market">
              <template v-slot="{ row }">
                {{ row.primary_market || 'Not specified' }}
              </template>
            </el-table-column>
            <el-table-column label="Total Leads" min-width="100" align="center">
              <template v-slot="{ row }">
                {{ row.leads_count || 0 }}
              </template>
            </el-table-column>
            <el-table-column label="HubSpot Owner ID" min-width="150" prop="hubspot_owner_id">
              <template v-slot="{ row }">
                {{ row.hubspot_owner_id || 'Not assigned' }}
              </template>
            </el-table-column>
            <el-table-column label="Created" min-width="120" align="center">
              <template v-slot="{ row }">
                {{ formatDate(row.created_at) }}
              </template>
            </el-table-column>
            <el-table-column label="Updated" min-width="120" align="center">
              <template v-slot="{ row }">
                {{ formatDate(row.updated_at) }}
              </template>
            </el-table-column>
            <el-table-column label="Actions" min-width="150" align="center">
              <template v-slot="{ row }">
                <base-button
                  @click.stop="openMergeModal(row)"
                  type="warning"
                  size="sm"
                  icon
                  title="Merge Company"
                  class="mr-1"
                >
                  <i class="fa fa-code-merge"></i>
                </base-button>
                <base-button
                  @click.stop="deleteCompany(row)"
                  type="danger"
                  size="sm"
                  icon
                  title="Delete Company"
                >
                  <i class="fa fa-trash"></i>
                </base-button>
              </template>
            </el-table-column>
          </el-table>

          <div
            slot="footer"
            class="row align-items-center justify-content-between"
          >
            <div class="col">
              <p class="card-category">
                Showing {{ from }} to {{ to }} of {{ total }} entries
              </p>
            </div>
            <base-pagination
              class="pagination-no-border"
              v-model="pagination.currentPage"
              :per-page="(meta && meta.per_page) || 15"
              :total="(meta && meta.total) || 0"
              @input="handlePageChange"
            >
            </base-pagination>
          </div>
        </card>
      </div>
    </div>

    <!-- New Company Modal -->
    <modal
      :show.sync="modalNewCompany"
      modal-classes="modal-dialog-centered modal-lg"
    >
      <template v-slot:header>
        <h5 class="modal-title">Create Company</h5>
      </template>

      <div class="px-4 py-4">
        <form role="form" @submit.prevent="createCompany">
          <div class="row">
            <div class="col-md-6">
              <base-input
                label="Company Name"
                v-model="form.company_name"
                placeholder="Company Name"
                required
                rules="required"
                name="company_name"
              >
              </base-input>
            </div>
            <div class="col-md-6">
              <base-input
                label="Industry"
              >
                <el-select
                  v-model="form.industry"
                  placeholder="Select Industry"
                  clearable
                  class="w-100"
                >
                  <el-option
                    v-for="industry in industries"
                    :key="industry"
                    :label="industry"
                    :value="industry"
                  >
                  </el-option>
                </el-select>
              </base-input>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <base-input
                label="Primary Market"
              >
                <el-select
                  v-model="form.primary_market"
                  placeholder="Select Primary Market"
                  clearable
                  class="w-100"
                >
                  <el-option
                    v-for="market in primaryMarkets"
                    :key="market.value"
                    :label="market.label"
                    :value="market.value"
                  >
                  </el-option>
                </el-select>
              </base-input>
            </div>
            <div class="col-md-6">
              <base-input
                label="HubSpot Owner"
              >
                <el-select
                  v-model="form.hubspot_owner_id"
                  placeholder="Select HubSpot Owner"
                  clearable
                  class="w-100"
                >
                  <el-option
                    v-for="owner in venueSpecialists"
                    :key="owner.hubspot_owner_id"
                    :label="owner.name"
                    :value="owner.hubspot_owner_id"
                  >
                  </el-option>
                </el-select>
              </base-input>
            </div>
          </div>

          <div class="text-right mt-4">
            <base-button
              type="primary"
              :loading="creating"
              @click="createCompany"
            >
              Save
            </base-button>
          </div>
        </form>
      </div>
    </modal>

    <!-- Company Merge Modal -->
    <company-merge-modal
      :show="modalMergeCompany"
      :selected-company="selectedCompanyForMerge"
      :companies="companiesData"
      @close="modalMergeCompany = false"
      @companies-merged="onCompaniesMerged"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import { Table, TableColumn, Select, Option, Notification, MessageBox } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination } from "@/components";
import CompanyMergeModal from "@/components/Companies/CompanyMergeModal";
import moment from 'moment';

export default {
  components: {
    BasePagination,
    RouteBreadCrumb,
    CompanyMergeModal,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
  },
  data() {
    return {
      loading: false,
      modalNewCompany: false,
      creating: false,
      pagination: {
        currentPage: 1,
        perPage: 15,
        total: 0
      },
      form: {
        company_name: '',
        industry: '',
        primary_market: '',
        hubspot_owner_id: ''
      },
      industries: [],
      primaryMarkets: [],
      // Merge modal
      modalMergeCompany: false,
      selectedCompanyForMerge: null,
      // Search functionality
      searchQuery: '',
      searchTimeout: null
    };
  },
  computed: {
    ...mapState("companies/", {
      companiesData: (state) => state.items,
      meta: (state) => state.meta || {},
      companiesIndustries: (state) => state.industries
    }),
    ...mapState("country/", {
      country: (state) => state.country || 'AU',
    }),
    // Use venue specialists data like in Leads.vue
    venueSpecialists() {
      let vcs = this.$store.state.venues.venueSpecialists;
      let data = [];
      for (let vc in vcs) {
        vc = vcs[vc];
        if(vc.hubspot_owner_id && vc.country.toUpperCase() === this.country) {
          data.push({
            label: vc.name,
            value: vc.hubspot_owner_id,
            hubspot_owner_id: vc.hubspot_owner_id,
            name: vc.name,
            active: vc.active,
            disabled: vc.disabled,
          });
        }
      }
      data.sort(function(a,b){
        a = a['label'];
        b = b['label'];
        return (a === b) ? 0 : (a < b) ? -1 : 1;
      });
      return data;
    },
    from() {
      return this.meta.from || 0;
    },
    to() {
      return this.meta.to || 0;
    },
    total() {
      return this.meta.total || 0;
    }
  },
  watch: {
    // Remove pagination watcher as it causes infinite loops
  },
  async created() {
    this.loading = true;
    await Promise.all([
      this.$store.dispatch("companies/getAll"),
      this.loadDropdownData(),
      this.$store.dispatch('venues/getVenueSpecialists') // Load venue specialists
    ]);
    this.loading = false;
  },
  methods: {
    selectCompany(company) {
      this.$router.push({
        name: "CompanyDetails",
        params: { id: company.id },
      });
    },
    formatDate(date) {
      return date ? moment(date).format('MMM DD, YYYY') : 'N/A';
    },
    async loadDropdownData() {
      try {
        // Load industries
        await this.$store.dispatch("companies/getIndustries");
        this.industries = this.companiesIndustries || [];
        
        // Load primary markets
        const marketsResponse = await this.$store.dispatch("companies/getPrimaryMarkets");
        this.primaryMarkets = marketsResponse || [];
      } catch (error) {
        console.error('Failed to load dropdown data:', error);
      }
    },
    async createCompany() {
      try {
        this.creating = true;
        await this.$store.dispatch('companies/createCompany', this.form);
        
        // Reset form and close modal
        this.form = {
          company_name: '',
          industry: '',
          primary_market: '',
          hubspot_owner_id: ''
        };
        this.modalNewCompany = false;
        
        // Refresh companies list
        await this.$store.dispatch("companies/getAll");
        
        Notification.success({
          message: 'Company created successfully!'
        });
      } catch (error) {
        Notification.error({
          message: 'Failed to create company'
        });
      } finally {
        this.creating = false;
      }
    },
    openMergeModal(company) {
      this.selectedCompanyForMerge = company;
      this.modalMergeCompany = true;
    },
    async onCompaniesMerged() {
      // Refresh companies list after merge
      await this.$store.dispatch("companies/getAll");
      this.modalMergeCompany = false;
      this.selectedCompanyForMerge = null;
    },
    async deleteCompany(company) {
      try {
        const confirmResult = await MessageBox.confirm(
          `Are you sure you want to delete "${company.company_name}"? This action cannot be undone.`,
          'Delete Company',
          {
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
            type: 'warning',
            confirmButtonClass: 'el-button--danger'
          }
        );

        if (confirmResult) {
          this.loading = true;
          await this.$store.dispatch('companies/deleteCompany', company.id);
          
          // Refresh companies list
          await this.performSearch(false);
          
          Notification.success({
            message: `Company "${company.company_name}" has been deleted successfully.`
          });
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Failed to delete company:', error);
          Notification.error({
            message: 'Failed to delete company. Please try again.'
          });
        }
      } finally {
        this.loading = false;
      }
    },
    onSearchInput() {
      // Clear existing timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = null;
      }

      // Debounce search with 300ms delay
      this.searchTimeout = setTimeout(() => {
        this.performSearch();
      }, 300);
    },
    async performSearch(resetPage = true) {
      try {
        this.loading = true;
        
        if (resetPage) {
          this.pagination.currentPage = 1; // Only reset to first page on new search
        }
        
        const params = {
          page: this.pagination.currentPage,
          per_page: this.pagination.perPage
        };

        if (this.searchQuery && this.searchQuery.trim().length > 0) {
          params.search = this.searchQuery.trim();
        }

        await this.$store.dispatch("companies/getAll", params);
      } catch (error) {
        console.error('Search failed:', error);
        Notification.error({
          message: 'Failed to search companies'
        });
      } finally {
        this.loading = false;
      }
    },
    async handlePageChange(page) {
      this.pagination.currentPage = page;
      await this.performSearch(false); // Don't reset page when changing pages
    },
    clearSearch() {
      this.searchQuery = '';
      this.performSearch();
    }
  },
  beforeDestroy() {
    // Clean up search timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
  }
};
</script>

<style scoped>
.name-col {
  color: #5e72e4;
  font-size: 0.875rem;
}
.name-col:hover {
  color: #233dd2;
  text-decoration: underline;
}
.table-striped tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.02);
}
</style>